import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { Newspaper, Stethoscope, Clock, ChevronLeft, ChevronRight, Loader2, Filter, Bookmark, UserPlus, ArrowRight, CheckCircle } from 'lucide-react';
import JumpToNav from '@/components/JumpToNav';

export default function InsightsHub() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [specialty, setSpecialty] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (specialty) params.append('specialty', specialty);
    params.append('page', page);
    params.append('limit', 18);
    api.get(`/insights?${params.toString()}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [specialty, page]);

  const title = specialty
    ? `${specialty} — Medical Insights by Doctors | DocSite`
    : 'Medical Insights — Articles by Practicing Doctors | DocSite';
  const description = 'Read medical insights written by practicing doctors on DocSite. Evidence-based health information from specialists who publish under a signed truthful-declaration consent.';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: window.location.href,
    publisher: { '@type': 'Organization', name: 'DocSite' },
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <link rel="canonical" href={window.location.href} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-50 via-white to-emerald-50 border-b border-slate-100">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-12 md:py-16">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3 flex items-center gap-1.5">
            <Newspaper size={12} /> Medical Insights
          </p>
          <h1 className="font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2.25rem, 3.6vw, 4.25rem)', lineHeight: 1.1 }} data-testid="insights-h1">
            Medical Insights
          </h1>
          <p className="text-slate-600 mt-4 max-w-3xl leading-relaxed" style={{ fontSize: 'clamp(1.05rem, 1.15vw, 1.3rem)' }}>
            Health articles written by practicing doctors. Evidence-based information — straight from specialists who publish under their own name and accountability.
          </p>

          <div className="mt-8 pt-6 border-t border-slate-200 space-y-4">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-slate-600 font-medium" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.2rem)' }} data-testid="insights-trust-row">
              <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Doctor-authored</span>
              <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Evidence-based</span>
              <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> No paid promotions</span>
            </div>

            <JumpToNav
              testId="insights-jump-nav"
              className="!mt-0 !pt-0 !border-0"
              items={[
                { icon: Filter, label: 'Filter by specialty', href: '#filters', color: 'sky' },
                { icon: Bookmark, label: 'Latest articles', href: '#articles', color: 'rose' },
                { icon: Stethoscope, label: 'Browse doctors', to: '/find/cardiology/mumbai', color: 'emerald' },
                { icon: UserPlus, label: 'Become a contributor', to: '/register?as=doctor&from=insights', color: 'violet' },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Filters */}
      <div id="filters" className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-6 border-b border-slate-100 scroll-mt-24">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-slate-500 font-medium">Filter:</span>
          <button onClick={() => { setSpecialty(''); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!specialty ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-sky-50'}`}
            data-testid="filter-all">
            All Specialties
          </button>
          {data?.specialties?.map(s => (
            <button key={s} onClick={() => { setSpecialty(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${specialty === s ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-sky-50'}`}
              data-testid={`filter-${s}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Articles Grid */}
      <section id="articles" className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-10 scroll-mt-24">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-sky-600" /></div>
        ) : data?.articles?.length === 0 ? (
          <div className="text-center py-20" data-testid="no-articles">
            <Newspaper size={40} className="text-slate-300 mx-auto mb-4" />
            <p className="text-lg text-slate-600">No articles published yet.</p>
            <p className="text-sm text-slate-500 mt-2">Doctors on DocSite are creating content every day. Check back soon!</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="articles-grid">
              {data.articles.map(art => (
                <Link key={art.slug + art.doctor_slug} to={`/dr/${art.doctor_slug}/articles/${art.slug}`}
                  className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-sky-200 hover:shadow-md transition-all group"
                  data-testid={`article-card-${art.slug}`}>
                  {art.primary_specialty && (
                    <p className="text-xs uppercase tracking-[0.15em] font-bold text-sky-600 mb-3 flex items-center gap-1.5">
                      <Stethoscope size={12} /> {art.primary_specialty}
                    </p>
                  )}
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900 group-hover:text-sky-700 transition-colors leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {art.title}
                  </h3>
                  {art.meta_description && (
                    <p className="text-sm text-slate-500 mt-3 line-clamp-3 leading-relaxed">{art.meta_description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-5 pt-4 border-t border-slate-100">
                    {art.doctor_photo ? (
                      <img src={art.doctor_photo} alt={art.doctor_name} className="w-9 h-9 rounded-full object-cover border border-slate-200" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center">
                        <Stethoscope size={16} className="text-sky-400" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{art.doctor_name}</p>
                      {art.published_at && (
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                          <Clock size={11} /> {new Date(art.published_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {data.pages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-10">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200 disabled:opacity-40 flex items-center gap-1">
                  <ChevronLeft size={14} /> Previous
                </button>
                <span className="text-sm text-slate-500">Page {page} of {data.pages}</span>
                <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page === data.pages}
                  className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200 disabled:opacity-40 flex items-center gap-1">
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-400">
        <p>Medical Insights on <Link to="/" className="text-sky-600 hover:underline">DocSite</Link> — Written by practicing doctors under a signed truthful-declaration consent.</p>
      </footer>
    </div>
  );
}
